import React, {useEffect, useRef, useState} from 'react';
import OdxModal from "../../../../OdxComponents/NativeOdx/Modal/Odx-Modal";
import OdxChip from "../../../../OdxComponents/NativeOdx/Odx-Chip";
import {odxIconSets, odxVariants} from "../../../../OdxComponents/constants";
import OdxIcon from "../../../../OdxComponents/NativeOdx/Odx-Icon";
import {UserManagementModalMode} from "../../../Hub/Interfaces/IUserManagement";
import {HubPathRouting} from "../../../../HubFramework/pathBuilder";
import {UpdateAccessRoles} from "../../../Hub/BackendFacade/apiCalls";
import {useMain} from "../../../../Utils/SessionControls/mainContext";
import {IAuthLevel, TenantLine} from "../../Interfaces/ITenantSystem";
import {defaultAuth} from "../../../Hub/constants";
import AddEditScopeModal from "./AddEditScopeModal";
import User_Delete from "./UserDelete";
import OdxInputField from "../../../../OdxComponents/NativeOdx/FormControls/Odx-InputField";
import {OdxInputError} from "../../../../OdxComponents/NativeOdx/FormControls/Interfaces";
import {isValidEmail} from "../../../../Utils/checker";
import {transformOdxLayoutRef} from "../../../../OdxComponents/odxLayoutRef";
import ModalTenantManagementTable from "../../../../OdxComponents/CustomOdx/ModalTenantManagementTable";
import {ScopeType} from "../../Utils/tenantSystem";
import {IUserData} from "../../Interfaces/IUsers";
import {IAppConfig} from "../../Interfaces/IConfig";
import {IScopeOverview} from "../../Interfaces/interfaces";
import {sendUsersEditDataCall} from "../../BackendFacade/apiCalls";
import {isUserroleSufficient} from "../../../../Utils/authorization";
import {UserRoles} from "../../../../Utils";
import {simplifyForHtmlId} from "../../../../Utils/transformer";

interface UserManagementModalProps {
    mode: UserManagementModalMode;
    onClose: () => void;
    onSuccessFunction: () => Promise<void>;
    projectId: string;
    userRow: IUserData;
    emailList: string[];
    hubOrIdms: "Hub" | "IDMS";
    appConfig: IAppConfig,
    scopeOverviewIdms: IScopeOverview;
}

const UserManagementModal: React.FC<UserManagementModalProps> = ({
                                                                     mode,
                                                                     onClose,
                                                                     onSuccessFunction,
                                                                     projectId,
                                                                     userRow,
                                                                     emailList,
                                                                     hubOrIdms,
                                                                     appConfig,
                                                                     scopeOverviewIdms

                                                                 }) => {

    const [tenantLines, setTenantLines] = useState<TenantLine[]>(userRow && userRow.tenantSystem ? userRow.tenantSystem : []);
    const [isAddScopeSideSheetOpen, setIsAddScopeSideSheetOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [editingScope, setEditingScope] = useState<TenantLine>(undefined);
    const [authLevel, setAuthLevel] = useState<string>(userRow?.authLevel || "");
    const [somethingWasEdited, setSomethingWasEdited] = useState<boolean>(false);
    const [discardChangesModal, setDiscardChangesModal] = useState<boolean>(false);

    // add user variables
    const [userIdentifier, setUserIdentifier] = React.useState(userRow?.id || userRow?.userId || userRow?.mail || "");
    const [userIdentifierMail, setUserIdentifierMail] = React.useState(userRow?.mail || userRow?.id || userRow?.userId || "");
    const [userList, setUserList] = React.useState<string[]>([]);
    const [emailError, setEmailError] = React.useState<OdxInputError>(undefined);

    const {setLoading, createToast, createErrorToast} = useMain();

    // todo: currentProject = currentProject.moduleInstanceId??????
    const currentProject = HubPathRouting.currentProject?.id || "null";
    if (currentProject != "null" && currentProject != projectId && projectId) throw new Error("????");

    const containerRef = useRef<HTMLDivElement>(null);

    const rootScope = "user";

    useEffect(() => {
        if (containerRef.current) {
            transformOdxLayoutRef(containerRef.current)
        }
    }, [containerRef])

    useEffect(() => {
        if (mode === UserManagementModalMode.Add) {
            // add "default" scope for new users so it's not so empty
            addGlobalScope()
        }
    }, []);

    useEffect(() => {
        if (somethingWasEdited) {
            setDiscardChangesModal(true);
            return
        }
        if (mode === UserManagementModalMode.Edit) {
            resetUser()
        }
        }, [userRow]);

    const resetUser = () => {
            // reset all variables
            setTenantLines(userRow?.tenantSystem || []);
            setUserIdentifier(userRow?.id || userRow?.userId || userRow?.mail || "");
            setUserIdentifierMail(userRow?.mail || userRow?.id || userRow?.userId || "");
            setIsAddScopeSideSheetOpen(false);
            setIsDeleteModalOpen(false);
            setUserList([]);
            setEmailError(undefined);
            setEditingScope(undefined);
            setAuthLevel(userRow?.authLevel || "");
            setSomethingWasEdited(false);

    }

    const addGlobalScope = () => {
        // project permissions may not see/edit/set the global hub permission
        if (isOnProjectLevel()) return;

        // global/root scope already present
        if (tenantLines.some((x: TenantLine) => x.fullscope === rootScope)) {
            return;
        }

        const newLine: TenantLine = {
            id: tenantLines.length,
            scopes: [rootScope],
            value: defaultAuth,
            tmp: "", // todo: WTF!?
            fullscope: rootScope // todo: this should be a function or something, calculated on demand using the scopes array
        };

        // project scopes -> add project to scope, otherwise user has global role
        if (isOnProjectLevel()) {
            newLine.scopes.push(currentProject);
            // todo: fullscope update missing?
        }

        setTenantLines([newLine]);
        setSomethingWasEdited(true);
    };

    const saveUser = async () => {
        // when ADDING, we can add many ppl at once, they are stored in the userList (list of strings, each is their ID/mail)
        // if we EDIT a user, their existing ID is in "userIdentifier".
        // TL;DR: userList always contains the full list of all userIDs we want to edit
        if (userIdentifier !== "") {
            userList.push(userIdentifier);
        }

        // object, for tenantMng, string for simple IDMS
        let newAuthValue: string = readNewAuthValueFromGui();

        setLoading(true);
        const errors: string[] = [];

        if (isIdmsModuleMode()) {
            if (hasAnyKindOfTenantManagements()) {
                newAuthValue = JSON.stringify(newAuthValue)
            } else {
                newAuthValue = authLevel;
            }

            const apiBody = {
                value: userList.map((user: string) => {
                    return {
                        id: user,
                        newValue: newAuthValue // <-- STRING of json!!! (or direct role like "admin")
                    }
                })
            }

            const data = await sendUsersEditDataCall(HubPathRouting.currentProject.id, HubPathRouting.currentModule.id, apiBody);
            if (!data.success) {
                errors.push(data.message);
            }
        } else {
            // hub

            const proms = userList
                .filter(x => x != "") // todo: how can this happen?
                .map((userId: string) => {

                    const prom = UpdateAccessRoles(userId, newAuthValue, projectId);
                    prom.then(async (data) => {
                        if (!data.success) {
                            errors.push(userId + ": " + data.message);
                            console.error("Error while saving user: " + data.message);
                        }
                    });
                    return prom;
                });

            await Promise.all(proms);
        }

        setLoading(false);

        if (errors.length > 0) {
            createErrorToast("Error while saving user: " + errors.join(", "));
        } else {
            onSuccessFunction();
            setTenantLines([]);
            onClose();
            createToast({message: "User saved successfully", variant: "success", title: "Success"});
        }
    };

    const readNewAuthValueFromGui = (): IAuthLevel => {
        const newAuthValue: IAuthLevel = {}; // todo: this could be a string for non-tenant cases too

        // read from tenantLines variable and insert into local newAuthValue object
        if (hasAnyKindOfTenantManagements()) {
            // remove empty/invalid lines and convert tenantLines-array into newAuthValue-object
            tenantLines.filter((x: TenantLine) => x.value != "-" && x.scopes.length != 0).forEach((x: TenantLine) => newAuthValue[x.scopes.join("/")] = x.value);
        } else {
            // TODO: Save non tenant management
            console.warn("Save non tenant management");
        }

        // delete the hub user level on hub project level
        if (isOnProjectLevel()) {
            if (newAuthValue[rootScope]) console.error("how did this happen?"); // todo: why/how was this added in the first place?
            delete newAuthValue[rootScope];
        }

        return newAuthValue;
    }

    const removeScope = (index: number) => {
        setTenantLines(tenantLines.filter((_, i) => i !== index));
        setSomethingWasEdited(true);
    };

    const handleSaveScope = (newScope: TenantLine) => {
        // check if new full scope already exists
        if (!editingScope && tenantLines.some((scope: TenantLine) => scope.fullscope === newScope.fullscope)) {
            createErrorToast('User scope already exists');
            return;
        }

        if (editingScope) {
            // replace/update edited scope in tenantLines (in a kinda weird way)
            const updatedScopes = tenantLines.map((scope: TenantLine) => scope.id === editingScope.id ? newScope : scope);
            setTenantLines(updatedScopes);
        } else {
            // add newScope to list
            setTenantLines([...tenantLines, newScope]);
        }
        setSomethingWasEdited(true);

        setEditingScope(null);
        setIsAddScopeSideSheetOpen(false);
    };

    const handleSaveAuthlevel = (newAuthLevel: string) => {
        setAuthLevel(newAuthLevel);
        setSomethingWasEdited(true);
        setIsAddScopeSideSheetOpen(false);
    }

    const openEditScopeModal = (scope: TenantLine) => {
        setEditingScope(scope);
        setIsAddScopeSideSheetOpen(true);
    };

    const openDeleteModal = () => {
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        onClose();
    };

    const addNewUser = (user: string) => {
        if (!checkForValidEmail(user)) {
            return;
        }
        setUserList([...userList, user]);
        setUserIdentifier("");
    };

    const removeUser = (username: string) => {
        setUserList(userList.filter((user: string) => user !== username));
    };

    const checkForValidEmail = (user: string) => {
        const isValidEmail = isEmailValid(user);
        if (!isValidEmail.valid) {
            setEmailError({text: isValidEmail.text, isActive: true});
        } else {
            setEmailError(undefined);
        }
        return isValidEmail.valid;
    };

    const isEmailValid = (email: string) => {
        if (!email) {
            return ({text: "Email is required", valid: false});
        } else if (!isValidEmail(email)) {
            return ({text: "Invalid email format", valid: false});
        } else if (userList.includes(email)) {
            return ({text: "User already added", valid: false});
        } else if (emailList.includes(email)) {
            return ({text: "User with email already exists", valid: false});
        } else {
            return ({text: "", valid: true});
        }
    };

    const isIdmsModuleMode = () => {
        if (hubOrIdms === "IDMS") return true;
        else if (hubOrIdms === "Hub") return false;
        throw new Error("invalid usersTab mode: " + hubOrIdms);
    }

    const hasAnyKindOfTenantManagements = () => {
        return !isIdmsModuleMode() || !appConfig.IsTenantManagementDisabled;
    }

    const isOnProjectLevel = () => {
        if (isIdmsModuleMode()) return false;
        return currentProject !== "null";
        return !!HubPathRouting.currentProject;
        return projectId && projectId != "null";
    }

    // ==========================================================================================================
    // ==== MAIN PAGE CONTENT ===================================================================================
    // ==========================================================================================================

    const modalNamespace = hubOrIdms.toLowerCase();
    const modeLabel = mode === UserManagementModalMode.Add ? 'add' : mode === UserManagementModalMode.Edit ? 'edit' : 'view';
    const baseId = `${modalNamespace}-user-modal-${modeLabel}`;

    const EmailField = () => {
        switch (mode) {
            case UserManagementModalMode.Add:
                return (
                    <>
                        <div data-odxLayout={'grid'} className={'odx-padding-bottom-12'} id={`${baseId}-email-entry`}>
                            <div data-odxLayout={'9'}>
                                <OdxInputField
                                    isRequired={true}
                                    id={`${baseId}-email-input`}
                                    label={'Email'}
                                    value={userIdentifier}
                                    onChange={(event) => setUserIdentifier(event.target.value)}
                                    onBlur={() => checkForValidEmail(userIdentifier)}
                                    error={emailError}
                                />
                            </div>
                            <div
                                data-odxLayout={'3'}
                                className={` content-center flex flex-col justify-end h-full w-full pb-6`}
                            >
                                <odx-button
                                    id={`${baseId}-add-email-btn`}
                                    variant="accent"
                                    size="lg"
                                    type="button"
                                    onClick={() => addNewUser(userIdentifier)}
                                    className="float-right !mb-6"
                                >
                                    Add
                                </odx-button>
                            </div>
                        </div>
                        <div>
                            <div className="odxGridContainer flex-wrap" data-odxLayout='flex inline gap-small' id={`${baseId}-email-list`}>
                                {userList.map((user, index) => (
                                    <div key={index}>
                                        <OdxChip
                                            key={index}
                                            truncateText={true}
                                            content={user}
                                            removable={isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id)}
                                            size="small"
                                            variant={odxVariants.secondary}
                                            onRemove={() => removeUser(user)}
                                            id={`${baseId}-email-chip-${simplifyForHtmlId(user)}`}
                                        />
                                    </div>
                                ))}
                                {userList.length > 0 && (
                                    <odx-button
                                        variant="ghost"
                                        size="lg"
                                        type="button"
                                        onClick={() => setUserList([])}
                                    >
                                        Clear All
                                    </odx-button>
                                )}
                            </div>
                        </div>
                    </>
                );
            case UserManagementModalMode.Edit:
                return (
                    <>
                        <p className={"text-2xl font-extrabold odx-padding-bottom-8"} id={`${baseId}-email-label`}>Email</p>
                        <p className={"text-2xl font-extrabold odx-padding-bottom-8"} id={`${baseId}-email-value`}>{userIdentifierMail}</p>
                    </>
                );
        }
    };

    const Headline = () => {
        if (!appConfig) return <p className={"text-2xl font-extrabold odx-padding-bottom-8"}>Scopes</p>
        if (appConfig.IsTenantManagementDisabled) {
            return <p className={"text-2xl font-extrabold odx-padding-bottom-8"}>Role</p>
        }
        return <p className={"text-2xl font-extrabold odx-padding-bottom-8"}>Scopes</p>
    }

    return mode != UserManagementModalMode.None && (
        <>
            {discardChangesModal && (
                <OdxModal
                    variant="default"
                    size="small"
                    title="Discard changes"
                    isOpen={discardChangesModal}
                    onClose={() => setDiscardChangesModal(false)}
                    footerButtons={[
                        <odx-button
                            key="close"
                            variant="neutral"
                            size="lg"
                            type="button"
                            onClick={() => setDiscardChangesModal(false)}
                        >
                            Close
                        </odx-button>,
                        <odx-button
                            key="confirm"
                            variant="primary"
                            size="lg"
                            type="button"
                            onClick={() => {
                                setDiscardChangesModal(false);
                                resetUser()
                            }}
                        >
                            Confirm
                        </odx-button>
                    ]}
                >
                    <div>
                        <p>Do you want to discard current changes and switch to another user?</p>
                    </div>
                </OdxModal>
            )}
            <div ref={containerRef} id={`${baseId}-view`}>
                {isAddScopeSideSheetOpen && <AddEditScopeModal
                    onClose={() => {
                        setIsAddScopeSideSheetOpen(false);
                        setEditingScope(null);
                    }}
                    onAddScope={handleSaveScope}
                    initialScope={editingScope}
                    isEditMode={!!editingScope}
                    globalScope={editingScope?.fullscope === 'user'}
                    scopeType={appConfig ? ScopeType.IDMS : ScopeType.Hub}
                    appConfig={appConfig}
                    scopeOverviewIdms={scopeOverviewIdms}
                    authLevel={authLevel}
                    setAuthLevel={handleSaveAuthlevel}
                />
                }
                {isDeleteModalOpen && <User_Delete
                    closeFunction={closeDeleteModal}
                    onSuccessFunction={onSuccessFunction}
                    userData={userRow}
                    currentProject={projectId}
                    hubOrIdms={hubOrIdms}
                />}
                <OdxModal
                    size="small"
                    title={mode == UserManagementModalMode.Add ? "Add User" : "Edit User"}
                    onClose={onClose}
                    isOpen={!isAddScopeSideSheetOpen}
                    variant="sidesheet"
                    footerButtons={[
                        mode == UserManagementModalMode.Edit && isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id) && (
                            <odx-button
                                key="delete"
                                id={`${baseId}-delete-btn`}
                                variant="danger"
                                size="lg"
                                type="button"
                                onClick={openDeleteModal}
                            >
                                Delete
                            </odx-button>
                        ),
                        <odx-button
                            key="cancel"
                            id={`${baseId}-cancel-btn`}
                            variant="ghost"
                            size="lg"
                            type="button"
                            onClick={onClose}
                        >
                            Cancel
                        </odx-button>,
                        isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id) && (
                            <odx-button
                                key="submit"
                                id={`${baseId}-submit-btn`}
                                variant="primary"
                                size="lg"
                                type="button"
                                onClick={() => saveUser()}
                            >
                                Submit
                            </odx-button>
                        )
                    ].filter(Boolean)}
                    id={baseId}
                >
                    {EmailField()}
                    <odx-separator style={{margin: '1rem 0'}} />
                    <Headline/>
                    <ModalTenantManagementTable
                        openEditScopeModal={openEditScopeModal}
                        scopes={tenantLines}
                        removeScope={removeScope}
                        disabled={isIdmsModuleMode()}
                        authLevel={authLevel}
                        appConfig={appConfig}
                    />
                    {!editingScope && !appConfig?.IsTenantManagementDisabled && (
                        <div className={"pt-5"}>
                            <odx-button
                                id={`${baseId}-add-scope-btn`}
                                variant="ghost"
                                size="lg"
                                type="button"
                                onClick={() => setIsAddScopeSideSheetOpen(true)}
                                className="!pl-0"
                            >
                                <odx-icon slot="prefix" name={`${odxIconSets.core}::plus`} size="md"></odx-icon>
                                Add Scope
                            </odx-button>
                        </div>
                    )}
                </OdxModal>
            </div>
        </>
    );
};

export default UserManagementModal;
